import { useMemo, useState } from "react";
import DataEntryKpiSchoolsModal from "@/components/DataEntryKpiSchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";
import {
  AlertTriangle,
  Check,
  Clock,
  Repeat,
  ShieldCheck,
} from "lucide-react";
import { BlockLink } from "@/components/DrilldownLink";
import DataEntryGuidanceChip from "@/components/DataEntryGuidanceChip";
import {
  bandWidthPct,
  barWidthPct,
  buildBlockPerformanceActionQueue,
  buildBlockPerformanceClosurePlan,
  buildBlockPerformanceFocus,
  buildBlockPerformanceNarrative,
  getEntryStabilityChip,
  getSnapshotRiskLabel,
  buildCompletionGaugeStyle,
  fmt,
  fmtLakhs,
  getCertificationBadge,
  getCompletionBadge,
  getHighestPendingBlockName,
  getOfficerPriority,
  getPendingBadge,
  getTopPendingBlocks,
  getTopRepeaterBlocks,
  isRiskRank,
  pct,
  round2,
} from "@/utils/dataEntryDerived";
import { toTitleCase } from "../utils/toTitleCase";

const bandMeta = [
  { key: "100%", label: "100%", tone: "" },
  { key: "95-99%", label: "95–99%", tone: "blue" },
  { key: "90-95%", label: "90–95%", tone: "orange" },
  { key: "<90%", label: "<90%", tone: "red" },
];

const InsightCard = ({ icon: Icon, iconTone, riskTone, title, value, unit, progressPct, progressColor, metaLeft, metaRight }) => (
  <article className="de-insight-card">
    <div className="de-insight-head">
      <div className={`de-icon-box ${iconTone}`}>
        <Icon size={22} />
      </div>
      <span className={`de-risk-dot ${riskTone}`} />
    </div>
    <h3>{toTitleCase(title)}</h3>
    <div className="de-insight-score">
      <strong>{value}</strong>
      <span>{unit}</span>
    </div>
    <div className="de-progress-line">
      <i style={{ width: `${Math.min(progressPct, 100)}%`, background: progressColor }} />
    </div>
    <div className="de-meta">
      <span>{metaLeft}</span>
      <span>{metaRight}</span>
    </div>
  </article>
);

const DataEntryBlockPerformanceTab = ({
  overview,
  completionBands,
  blockData,
  guidance,
  guidanceProvider,
  guidanceLoading,
  onNavigateTab,
  scopeLabel = "Pune District",
}) => {
  const [kpiMetricModal, setKpiMetricModal] = useState(null);
  const topPendingBlocks = useMemo(() => getTopPendingBlocks(blockData), [blockData]);
  const topRepeaterBlocks = useMemo(() => getTopRepeaterBlocks(blockData), [blockData]);
  const localActionQueue = useMemo(
    () => buildBlockPerformanceActionQueue(overview, topRepeaterBlocks, topPendingBlocks),
    [overview, topRepeaterBlocks, topPendingBlocks]
  );
  const localClosurePlan = useMemo(() => buildBlockPerformanceClosurePlan(overview), [overview]);
  const actionQueue = guidance?.action_queue?.length ? guidance.action_queue : localActionQueue;
  const closurePlan = guidance?.closure_plan?.length ? guidance.closure_plan : localClosurePlan;
  const narrative = guidance?.narrative ?? buildBlockPerformanceNarrative(overview);
  const primaryFocus = guidance?.primary_focus ?? buildBlockPerformanceFocus(topRepeaterBlocks, topPendingBlocks);
  const stabilityChip = guidance?.entry_stability_label
    ? { label: guidance.entry_stability_label, tone: getEntryStabilityChip(overview).tone }
    : getEntryStabilityChip(overview);
  const totalSchools = overview?.total_schools ?? 0;
  const maxPending = topPendingBlocks[0]?.pending_students ?? 1;
  const maxRepeaters = topRepeaterBlocks[0]?.total_repeaters ?? 1;

  const scrollToRankings = () => {
    document.getElementById("de-block-rankings")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToActions = () => {
    document.getElementById("de-block-actions")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="de-block-tab" data-testid="data-entry-block-performance-tab">
      <section className="de-hero-card">
        <div className="de-hero-layout">
          <div>
            <div className="de-hero-main">
              <div
                className="de-completion-gauge"
                style={buildCompletionGaugeStyle(overview?.completion_rate ?? 0)}
                aria-label={`Completion ${overview?.completion_rate ?? 0} percent`}
              >
                <div>
                  <strong>{pct(overview?.completion_rate ?? 0, 2)}</strong>
                  <span>Completion</span>
                </div>
              </div>
              <div className="de-hero-copy">
                <h2>Block Performance Command Centre</h2>
                <p>{narrative}</p>
                <div className="de-status-row">
                  <DataEntryGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
                  <HeroStatusChip chipClass="de-status-chip" variant={stabilityChip.tone}>
                    {stabilityChip.label}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="de-status-chip"
                    variant="red"
                    onClick={() => setKpiMetricModal("non_certified")}
                    ariaLabel={`${overview?.non_certified_schools ?? 0} non-certified schools — view awaiting certification`}
                  >
                    {fmt(overview?.non_certified_schools ?? 0)} Non-certified Schools
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="de-status-chip"
                    variant="amber"
                    onClick={() => setKpiMetricModal("pending_students")}
                    ariaLabel={`${overview?.pending_students ?? 0} pending students — view schools with open records`}
                  >
                    {fmt(overview?.pending_students ?? 0)} Pending Students
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="de-summary-note">
              <div className="de-icon-box orange">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary action focus</b>
                <span>{primaryFocus}</span>
              </div>
            </div>
          </div>

          <div className="de-metric-grid">
            <article className="de-metric-card green">
              <small>Total Students</small>
              <strong>{fmtLakhs(overview?.total_students ?? 0)}</strong>
              <span>Across {fmt(overview?.total_schools ?? 0)} schools</span>
            </article>
            <article className="de-metric-card orange">
              <small>Pending Students</small>
              <strong>{fmt(overview?.pending_students ?? 0)}</strong>
              <span>{pct(overview?.pending_rate ?? 0, 2)} still pending</span>
            </article>
            <article className="de-metric-card purple">
              <small>Certified Schools</small>
              <strong>{pct(overview?.certification_rate ?? 0, 1)}</strong>
              <span>
                {fmt(overview?.certified_schools ?? 0)} of {fmt(overview?.total_schools ?? 0)} completed
              </span>
            </article>
            <article className="de-metric-card red">
              <small>Repeaters</small>
              <strong>{fmt(overview?.total_repeaters ?? 0)}</strong>
              <span>Requires validation</span>
            </article>
          </div>
        </div>
      </section>

      <div className="de-section-title">
        <div>
          <h2>Decision Snapshot</h2>
          <p>Cards separate what is healthy, what is delayed, and what requires officer intervention.</p>
        </div>
        <button type="button" className="de-section-action" onClick={scrollToRankings}>
          View rankings →
        </button>
      </div>

      <section className="de-insight-grid">
        <InsightCard
          icon={Check}
          iconTone="green"
          riskTone=""
          title="Data Entry Completion"
          value={round2(overview?.completion_rate ?? 0)}
          unit="%"
          progressPct={overview?.completion_rate ?? 0}
          progressColor="var(--de-success)"
          metaLeft={`Only ${fmt(overview?.pending_students ?? 0)} pending`}
          metaRight={getSnapshotRiskLabel("completion", overview?.completion_rate ?? 0, overview)}
        />
        <InsightCard
          icon={ShieldCheck}
          iconTone="red"
          riskTone="red"
          title="Certification Readiness"
          value={round2(overview?.certification_rate ?? 0)}
          unit="%"
          progressPct={overview?.certification_rate ?? 0}
          progressColor="var(--de-danger)"
          metaLeft={`${fmt(overview?.non_certified_schools ?? 0)} non-certified`}
          metaRight={getSnapshotRiskLabel("certification", overview?.certification_rate ?? 0, overview)}
        />
        <InsightCard
          icon={Repeat}
          iconTone="orange"
          riskTone="amber"
          title="Repeater Concentration"
          value={round2(overview?.repeater_rate ?? 0)}
          unit="%"
          progressPct={Math.min(100, (overview?.repeater_rate ?? 0) * 10)}
          progressColor="var(--de-warning)"
          metaLeft={`${fmt(overview?.total_repeaters ?? 0)} cases`}
          metaRight={getSnapshotRiskLabel("repeater", overview?.repeater_rate ?? 0, overview)}
        />
        <InsightCard
          icon={Clock}
          iconTone="purple"
          riskTone="amber"
          title="Pending Load"
          value={fmt(overview?.pending_students ?? 0)}
          unit="students"
          progressPct={barWidthPct(overview?.pending_students ?? 0, maxPending)}
          progressColor="var(--de-purple)"
          metaLeft={`${getHighestPendingBlockName(blockData)} highest`}
          metaRight={getSnapshotRiskLabel("pending", overview?.pending_rate ?? 0, overview)}
        />
      </section>

      <div className="de-section-title">
        <div>
          <h2>Block Diagnostics</h2>
          <p>Chart areas are given enough width, with officer-readable labels and clearly separated risk themes.</p>
        </div>
        <button type="button" className="de-section-action" onClick={scrollToActions}>
          Go to actions →
        </button>
      </div>

      <section className="de-analytics-grid">
        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>Highest Pending-Load Blocks</h3>
              <p>Small remaining pending counts should be closed through block-specific follow-up.</p>
            </div>
            <span className="de-pill">Pending</span>
          </div>
          <div className="de-bar-list">
            {topPendingBlocks.map((block) => (
              <div className="de-hbar-row" key={block.block_name}>
                <div className="de-hbar-label">{block.block_name}</div>
                <div className="de-hbar-track">
                  <div
                    className="de-hbar orange"
                    style={{ width: `${barWidthPct(block.pending_students ?? 0, maxPending)}%` }}
                  />
                </div>
                <div className="de-hbar-value">{fmt(block.pending_students ?? 0)}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>School Completion Bands</h3>
              <p>Most schools are at 100% entry, but certification remains the true bottleneck.</p>
            </div>
            <span className="de-pill">Completion %</span>
          </div>
          <div className="de-band-grid">
            {bandMeta.map(({ key, label, tone }) => {
              const value = completionBands?.[key] ?? 0;
              return (
                <div className="de-band-row" key={key}>
                  <div className="de-band-label">{label}</div>
                  <div className="de-band-track">
                    <div
                      className={`de-band-fill ${tone}`}
                      style={{ width: `${bandWidthPct(value, totalSchools)}%` }}
                    />
                  </div>
                  <div className="de-band-value">{fmt(value)}</div>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <section className="de-panel de-table-card" id="de-block-rankings">
        <div className="de-table-head">
          <div>
            <h3>Block-Wise Performance Ranking</h3>
            <p>Ranked for operational review using completion %, pending count, repeater load and certification rate.</p>
          </div>
          <span className="de-pill">{fmt(blockData?.length ?? 0)} blocks</span>
        </div>
        <div className="de-table-wrap">
          <table className="de-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Block</th>
                <th className="de-num">Schools</th>
                <th className="de-num">Students</th>
                <th className="de-num">Completed</th>
                <th className="de-num">Pending</th>
                <th className="de-num">Completion</th>
                <th className="de-num">Repeaters</th>
                <th className="de-num">Certified</th>
                <th>Officer Priority</th>
              </tr>
            </thead>
            <tbody>
              {blockData.map((block) => {
                const completionBadge = getCompletionBadge(block.completion_pct ?? 0);
                const pendingBadge = getPendingBadge(block.pending_students ?? 0);
                const certBadge = getCertificationBadge(block.certification_rate ?? 0);
                const officerPriority = getOfficerPriority(block);
                const risk = isRiskRank(block);

                return (
                  <tr key={block.block_name}>
                    <td>
                      <span className={`de-rank ${risk ? "risk" : ""}`}>{block.rank}</span>
                    </td>
                    <td>
                      <BlockLink blockCode={block.block_code} blockName={block.block_name}>
                        {block.block_name}
                      </BlockLink>
                    </td>
                    <td className="de-num">{fmt(block.total_schools ?? 0)}</td>
                    <td className="de-num">{fmt(block.total_students ?? 0)}</td>
                    <td className="de-num de-green-text">{fmt(block.total_completed ?? 0)}</td>
                    <td className="de-num">
                      <span className={`de-badge ${pendingBadge.tone}`}>{pendingBadge.label}</span>
                    </td>
                    <td className="de-num">
                      <span className={`de-badge ${completionBadge.tone}`}>{completionBadge.label}</span>
                    </td>
                    <td className="de-num de-red-text">{fmt(block.total_repeaters ?? 0)}</td>
                    <td className="de-num">
                      <span className={`de-badge ${certBadge.tone}`}>{certBadge.label}</span>
                    </td>
                    <td>
                      <span className={`de-badge ${officerPriority.tone}`}>{officerPriority.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="de-two-column" id="de-block-actions">
        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>Repeater Concentration</h3>
              <p>Blocks with the largest repeater volumes need sample validation before certification push.</p>
            </div>
            <span className="de-pill">Repeaters</span>
          </div>
          <div className="de-bar-list">
            {topRepeaterBlocks.map((block) => (
              <div className="de-hbar-row" key={block.block_name}>
                <div className="de-hbar-label">{block.block_name}</div>
                <div className="de-hbar-track">
                  <div
                    className="de-hbar red"
                    style={{ width: `${barWidthPct(block.total_repeaters ?? 0, maxRepeaters)}%` }}
                  />
                </div>
                <div className="de-hbar-value">{fmt(block.total_repeaters ?? 0)}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="de-panel">
          <div className="de-panel-head">
            <div>
              <h3>Officer Action Queue</h3>
              <p>Practical next steps for district review and block-level execution.</p>
            </div>
            <span className="de-pill">Next 30 days</span>
          </div>
          <div className="de-action-card">
            {actionQueue.map((item) => (
              <div className="de-action-item" key={item.title}>
                <div className={`de-icon-box ${item.tone}`}>
                  {item.tone === "red" ? (
                    <ShieldCheck size={22} />
                  ) : item.tone === "orange" ? (
                    <Repeat size={22} />
                  ) : (
                    <Clock size={22} />
                  )}
                </div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="de-panel">
        <div className="de-panel-head">
          <div>
            <h3>30-Day Data Governance Closure Plan</h3>
            <p>Sequenced actions convert a crowded monitoring screen into an operational closure workflow.</p>
          </div>
          <span className="de-pill">Execution plan</span>
        </div>
        <div className="de-timeline">
          {closurePlan.map((step) => (
            <div className="de-step" key={step.week}>
              <b>{step.week}</b>
              <div>
                <strong>{step.title}</strong>
                <span>{step.text}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <DataEntryKpiSchoolsModal
        open={Boolean(kpiMetricModal)}
        metric={kpiMetricModal}
        onClose={() => setKpiMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={
          kpiMetricModal === "non_certified"
            ? overview?.certification_rate
            : kpiMetricModal === "pending_students"
              ? overview?.completion_rate
              : null
        }
        districtAggregate={kpiMetricModal === "pending_students" ? overview?.pending_students : null}
      />
    </div>
  );
};

export default DataEntryBlockPerformanceTab;
